<script setup>
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  toRefs,
  watch,
} from 'vue';

import { useViewportActions } from '@/src/components/pixel-overlay/useViewportActions';

import { useViewportStateStore } from '../store/contours/viewport-state-store';
import { useCurrentStudyStore } from '../store/dicom-web/current-study-store';
import { useViewCameraStore } from '../store/view-configs/camera';

import ImageStripSlider from './ImageStripSlider.vue';
import ThumbnailContext from './thumbnail-viewer/ThumbnailContext.vue';
import ThumbnailImage from './thumbnail-viewer/ThumbnailImage.vue';

const props = defineProps({
  viewerID: { type: String },
});
const { viewerID } = toRefs(props);

// Reactive data
const imageHeight = ref(150);
const visibleImageIndx = ref([]);
const resizeObserver = ref(null);
const isMouseInside = ref(false);

// Context menu and selection state
const contextMenuOptions = ref({
  show: false,
  x: 0,
  y: 0,
  imageUID: '',
  seriesIdx: 0,
  sliceIdx: 0,
  imageIdx: 0,
  selectedImages: [],
});

// Store instances
const viewportStateStore = useViewportStateStore();
const currentStudyStore = useCurrentStudyStore();
const viewCameraStore = useViewCameraStore();
const viewportActions = useViewportActions();

// Template refs
const imageBarContainer = ref(null);
const thumbImages = ref([]);
const sliderBar = ref(null);

// Reactive state
const imageBarContainerWidth = ref(0);
const showThumbnails = ref(true);
const scrollAmount = ref(0);
const scrollAmountSlider = ref(0);
const selectedImages = ref([]);
const lastSelectedIndex = ref(null);
const startZoom = ref(null);

const additionalImages = ref(0);
let lastMoveXTime = 0;
const throttleDelay = 50;
let isStripSliding = false;

// Utility functions
const adjustDivWidth = () => {
  if (imageBarContainer.value) {
    imageBarContainerWidth.value
      = imageBarContainer.value.getBoundingClientRect().width;
  }
  else {
    imageBarContainerWidth.value = 0;
  }
};

const handleResize = () => {
  adjustDivWidth();
};

// Computed properties
const currentImageIndices = computed(() =>
  viewportStateStore.getSelectedImageIndices(viewerID.value),
);

const sliceObj = computed(() =>
  viewportActions.getStudyObjectByViewerId(viewerID.value),
);

const currentSeriesIndex = computed(() => currentImageIndices.value.seriesIdx);
const currentSliceIndex = computed(() => currentImageIndices.value.sliceIdx);
const currentPhase = computed(() => currentImageIndices.value.imageIdx);

const numImages = computed(() =>
  currentStudyStore.getImagesCount(
    currentSeriesIndex.value,
    currentSliceIndex.value,
  ),
);

const curImageID = computed(() =>
  currentStudyStore.getImageUID(currentImageIndices.value),
);

const cameraConfig = computed(() =>
  viewCameraStore.getConfig(viewerID.value, curImageID.value),
);

// Methods
const imageUIDFromIndex = (index) => {
  return currentStudyStore.getImageUID({
    seriesIdx: currentSeriesIndex.value,
    sliceIdx: currentSliceIndex.value,
    imageIdx: index,
  });
};

const isImageIdxSelected = (imageIndex) => {
  const imageUID = imageUIDFromIndex(imageIndex);
  return selectedImages.value.includes(imageUID);
};

const handleContextMenu = (e) => {
  contextMenuOptions.value.show = false;
  setTimeout(() => {
    contextMenuOptions.value.x = e.x;
    contextMenuOptions.value.y = e.y;
    contextMenuOptions.value.imageUID = e.imageUID;
    contextMenuOptions.value.seriesIdx = e.indexes.seriesIdx;
    contextMenuOptions.value.sliceIdx = e.indexes.sliceIdx;
    contextMenuOptions.value.imageIdx = e.indexes.imageIdx;
    contextMenuOptions.value.selectedImages = selectedImages.value;
    contextMenuOptions.value.show = true;
  }, 10);
};

const handleImageClick = (payload) => {
  const { imageIndex, ctrlKey, shiftKey } = payload;
  const clickedImageUID = imageUIDFromIndex(imageIndex);

  if (ctrlKey) {
    if (selectedImages.value.includes(clickedImageUID)) {
      selectedImages.value = selectedImages.value.filter(
        uid => uid !== clickedImageUID,
      );
    }
    else {
      selectedImages.value.push(clickedImageUID);
    }
    lastSelectedIndex.value = imageIndex;
  }
  else if (shiftKey && lastSelectedIndex.value === null) {
    selectedImages.value.push(clickedImageUID);
    lastSelectedIndex.value = imageIndex;
  }
  else if (shiftKey && lastSelectedIndex.value !== null) {
    const start = Math.min(lastSelectedIndex.value, imageIndex);
    const end = Math.max(lastSelectedIndex.value, imageIndex);
    const rangeSelection = [];
    for (let i = start; i <= end; i++) {
      const asImageUID = imageUIDFromIndex(i);
      if (!selectedImages.value.includes(asImageUID)) {
        rangeSelection.push(asImageUID);
      }
    }
    selectedImages.value.push(...rangeSelection);
  }
  else {
    selectedImages.value = [];
    lastSelectedIndex.value = null;
  }
};

const updateVisibleImageIndx = () => {
  const indexArray = [];
  const targetContainer
    = imageBarContainer.value?.querySelector('[ref="image-container"]')
      || imageBarContainer.value?.querySelector('.strip');

  if (!targetContainer) {
    return;
  }

  if (!scrollAmount.value) {
    scrollAmount.value = 0;
  }

  const containerWidth = targetContainer.getBoundingClientRect().width;
  const canFitImages = Math.floor(containerWidth / imageHeight.value);
  const remainingWidth = containerWidth - canFitImages * imageHeight.value;
  additionalImages.value
    = remainingWidth > 0 ? Math.ceil(remainingWidth / imageHeight.value) : 0;
  const index = Math.max(0, Math.floor(scrollAmount.value / imageHeight.value));

  for (
    let i = index;
    i < index + canFitImages + additionalImages.value && i < numImages.value;
    i++
  ) {
    indexArray.push(i);
  }

  visibleImageIndx.value = indexArray;
};

const scrollX = (e) => {
  // Set selected image in viewport store
  const phaseNumber = Math.max(
    Math.min(currentPhase.value + Math.sign(e.deltaY) * 1, numImages.value - 1),
    0,
  );
  viewportStateStore.setSelectedImageIndices(viewerID.value, {
    seriesIdx: currentSeriesIndex.value,
    sliceIdx: currentSliceIndex.value,
    imageIdx: phaseNumber,
  });
};

const handleKeyDown = (e) => {
  if (!isMouseInside.value) return;

  if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
    e.preventDefault();
    const direction = e.key === 'ArrowRight' ? 1 : -1;
    const phaseNumber = Math.max(
      Math.min(currentPhase.value + direction, numImages.value - 1),
      0,
    );
    viewportStateStore.setSelectedImageIndices(viewerID.value, {
      seriesIdx: currentSeriesIndex.value,
      sliceIdx: currentSliceIndex.value,
      imageIdx: phaseNumber,
    });
  }
};

const scrollToPhase = (phase) => {
  if (phase >= numImages.value) {
    return;
  }
  const containerElement = imageBarContainer.value?.querySelector('.strip');
  if (!containerElement) return;

  scrollAmount.value = phase * imageHeight.value;

  if (
    scrollAmount.value
    > (numImages.value - visibleImageIndx.value.length) * imageHeight.value
  ) {
    containerElement.scrollLeft = scrollAmount.value;
  }
  else {
    containerElement.scrollLeft = 0;
  }
  scrollAmountSlider.value = Math.max(0, scrollAmount.value);

  scrollAmount.value = Math.max(
    0,
    Math.min(
      (numImages.value - visibleImageIndx.value.length) * imageHeight.value,
      scrollAmount.value,
    ),
  );
};

const changeImageHeight = (newHeight) => {
  adjustDivWidth();
  scrollToPhase(0);
  imageHeight.value = newHeight;
  updateVisibleImageIndx();
};

const moveX = (dx) => {
  const containerElement = imageBarContainer.value?.querySelector('.strip');
  if (!containerElement) return;

  isStripSliding = true;
  scrollAmountSlider.value = dx;
  scrollAmount.value
    = Math.floor(scrollAmountSlider.value / imageHeight.value)
      * imageHeight.value;

  if (
    scrollAmount.value
    > (numImages.value - visibleImageIndx.value.length) * imageHeight.value
  ) {
    containerElement.scrollLeft = scrollAmount.value;
  }
  else {
    containerElement.scrollLeft = 0;
  }

  scrollAmount.value = Math.max(
    0,
    Math.min(
      (numImages.value - visibleImageIndx.value.length) * imageHeight.value,
      scrollAmount.value,
    ),
  );

  const now = Date.now();
  if (now - lastMoveXTime < throttleDelay) {
    setTimeout(() => {
      isStripSliding = false;
    }, throttleDelay);
    return;
  }
  lastMoveXTime = now;

  // Set selected image in viewport store
  const phaseNumber = Math.max(
    Math.min(Math.floor(dx / imageHeight.value), numImages.value - 1),
    0,
  );
  viewportStateStore.setSelectedImageIndices(viewerID.value, {
    seriesIdx: currentSeriesIndex.value,
    sliceIdx: currentSliceIndex.value,
    imageIdx: phaseNumber,
  });

  setTimeout(() => {
    isStripSliding = false;
  }, throttleDelay);
};

// Lifecycle hooks
onMounted(async () => {
  adjustDivWidth();
  window.addEventListener('resize', handleResize);
  window.addEventListener('keydown', handleKeyDown);

  await nextTick();

  const targetContainer = imageBarContainer.value?.querySelector('.strip');
  if (targetContainer) {
    resizeObserver.value = new ResizeObserver(() => {
      updateVisibleImageIndx();
    });

    resizeObserver.value.observe(targetContainer);
    updateVisibleImageIndx();
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('keydown', handleKeyDown);
  if (resizeObserver.value) {
    resizeObserver.value.disconnect();
  }
});

// Watchers
watch(cameraConfig, (newVal) => {
  if (newVal && startZoom.value === null) {
    startZoom.value = newVal;
  }
});

watch(currentPhase, (newValue) => {
  // Skip if user is scrolling via slider to prevent feedback loop
  if (isStripSliding) return;

  const targetContainer = imageBarContainer.value?.querySelector('.strip');
  if (!targetContainer) return;

  const containerWidth = targetContainer.getBoundingClientRect().width;
  const canFitImages = Math.floor(containerWidth / imageHeight.value);
  const inViewportPhases = visibleImageIndx.value.slice(0, canFitImages);

  if (!inViewportPhases.includes(newValue)) {
    const lowestValue = inViewportPhases[0];
    const isSmaller = newValue < lowestValue;

    if (isSmaller) {
      targetContainer.scrollLeft = 0;
      scrollToPhase(Math.min(newValue, numImages.value));
    }
    else {
      scrollToPhase(
        Math.min(
          newValue - canFitImages + additionalImages.value,
          numImages.value,
        ),
      );
    }
  }
});

watch(scrollAmount, () => {
  updateVisibleImageIndx();
});

watch(numImages, () => {
  updateVisibleImageIndx();
});
</script>

<template>
  <!-- eslint-disable-next-line vuejs-accessibility/mouse-events-have-key-events -->
  <div
    ref="imageBarContainer"
    class="image-bar-container"
    @mouseenter="isMouseInside = true"
    @mouseleave="isMouseInside = false"
  >
    <div
      v-if="isMouseInside || !showThumbnails"
      style="position: absolute; top: 2px; right: 2px; z-index: 99999"
      class="simple-panel rounded-none border-transparent"
    >
      <v-btn
        size="small"
        variant="text"
        style="min-width: 0"
        class="px-2"
        @click="showThumbnails = !showThumbnails"
      >
        <v-icon v-if="showThumbnails" class="default-icon">
          mdi mdi-arrow-collapse-vertical
        </v-icon>
        <v-icon v-else class="default-icon">
          mdi mdi-arrow-expand-vertical
        </v-icon>
        <v-tooltip location="top" activator="parent">
          {{ showThumbnails ? 'Hide Thumbnails' : 'Show Thumbnails' }}
        </v-tooltip>
      </v-btn>
      <v-btn
        size="small"
        variant="text"
        style="min-width: 0"
        class="px-2"
        @click="changeImageHeight(50)"
      >
        <v-icon class="default-icon">
          mdi mdi-image-size-select-small
        </v-icon>

        <v-tooltip location="top" activator="parent">
          Small
        </v-tooltip>
      </v-btn>
      <v-btn
        size="small"
        variant="text"
        style="min-width: 0"
        class="px-2"
        @click="changeImageHeight(100)"
      >
        <v-icon class="default-icon">
          mdi mdi-image-size-select-small
        </v-icon>
        <v-tooltip location="top" activator="parent">
          Medium
        </v-tooltip>
      </v-btn>
      <v-btn
        size="small"
        variant="text"
        style="min-width: 0"
        class="px-2"
        @click="changeImageHeight(150)"
      >
        <v-icon class="default-icon">
          mdi mdi-image-size-select-large
        </v-icon>
        <v-tooltip location="top" activator="parent">
          Large
        </v-tooltip>
      </v-btn>
    </div>
    <!-- {{ visibleImageIndx }} , {{ scrollAmount }}, {{ scrollAmountSlider }} -->
    <div
      class="strip w-full"
      @mousewheel.passive="scrollX"
    >
      <div
        v-if="showThumbnails"
        :style="{ position: 'relative' }"
      >
        <div v-if="visibleImageIndx.length > 0" class="phase-images-container">
          <div v-for="(imageIdx, index) in visibleImageIndx" :key="index">
            <ThumbnailImage
              ref="thumbImages"
              :viewer-i-d="viewerID"
              :phase-number="imageIdx"
              :current-phase="currentPhase"
              :current-image-i-d="curImageID"
              :current-series-index="currentSeriesIndex"
              :current-slice-index="currentSliceIndex"
              :is-selected="isImageIdxSelected(imageIdx)"
              :slice-obj="sliceObj"
              :thumbnail-height="imageHeight"
              @open-context-menu="handleContextMenu"
              @image-click="handleImageClick"
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  <ImageStripSlider
    v-if="showThumbnails"
    ref="sliderBar"
    :num-images="numImages"
    :img-width="imageHeight"
    :slice-obj="sliceObj"
    :canvas-width="imageBarContainerWidth"
    :scroll-amount="scrollAmountSlider || 0"
    @move="moveX"
  />

  <ThumbnailContext
    :viewer-i-d="viewerID"
    :x="contextMenuOptions.x"
    :y="contextMenuOptions.y"
    :show="contextMenuOptions.show"
    :image-u-i-d="contextMenuOptions.imageUID"
    :current-series-index="contextMenuOptions.seriesIdx"
    :current-slice-index="contextMenuOptions.sliceIdx"
    :phase-number="contextMenuOptions.imageIdx"
    :selected-images="contextMenuOptions.selectedImages"
    @update:show="contextMenuOptions.show = false"
  />
</template>

<style scoped>
.image-bar-container {
  width: 100%;
  position: relative;
}

.strip {
  overflow: hidden;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
  border-radius: 2px;
  display: grid;
  grid-template-columns: 1fr;
  transform: all 0.3s;
}

.phase-images-container {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  width: fit-content;
}

.strip div {
  grid-row-start: 1;
  grid-column-start: 1;
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
